import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Upload, Download, AlertCircle, CheckCircle, XCircle, Info, Users as UsersIcon, RefreshCw } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';

interface ImportResult {
  email: string;
  status: 'success' | 'failed' | 'skipped';
  userId?: number;
  error?: string;
}

interface ImportResponse {
  success: boolean;
  message: string;
  results: {
    total: number;
    successful: number;
    failed: number;
    skipped: number;
    details: ImportResult[];
  };
}

interface ImportedUser {
  id: number;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  source: string | null;
  createdAt: string;
  lastLoginTimestamp: string | null;
  isActive: boolean;
}

export default function BulkImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState('bulk_import');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ImportResponse | null>(null);
  const [importedUsers, setImportedUsers] = useState<ImportedUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [filterSource, setFilterSource] = useState('');
  const [activeTab, setActiveTab] = useState('import');
  const { toast } = useToast();

  // Fetch imported users
  const fetchImportedUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log('[BULK IMPORT] Fetching imported users...');
      const response = await apiRequest('GET', '/api/bulk-import/imported-users');
      console.log('[BULK IMPORT] Response object:', response);
      
      const data = await response.json();
      console.log('[BULK IMPORT] Received data:', JSON.stringify(data, null, 2));
      console.log('[BULK IMPORT] Users array:', data?.users);
      console.log('[BULK IMPORT] Users count:', data?.users?.length || 0);
      console.log('[BULK IMPORT] First user:', data?.users?.[0]);
      
      const users = Array.isArray(data?.users) ? data.users : [];
      console.log('[BULK IMPORT] Setting imported users:', users);
      setImportedUsers(users);
      
      if (users.length > 0) {
        toast({
          title: 'Users loaded',
          description: `Found ${users.length} imported user(s)`,
        });
      } else {
        console.warn('[BULK IMPORT] No users in response despite successful fetch');
      }
    } catch (error: any) {
      console.error('[BULK IMPORT] Failed to fetch imported users:', error);
      toast({
        title: 'Failed to load users',
        description: error.message || 'Could not fetch imported users',
        variant: 'destructive',
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load imported users when switching to that tab
  useEffect(() => {
    if (activeTab === 'view') {
      fetchImportedUsers();
    }
  }, [activeTab]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const fileType = selectedFile.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(fileType || '')) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a CSV or Excel file',
          variant: 'destructive',
        });
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const users = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const user: any = {};
      
      headers.forEach((header, index) => {
        if (values[index]) {
          user[header] = values[index];
        }
      });

      // Map common header variations
      if (!user.email && user.email_address) user.email = user.email_address;
      if (!user.firstName && user.first_name) user.firstName = user.first_name;
      if (!user.firstName && user.firstname) user.firstName = user.firstname;
      if (!user.lastName && user.last_name) user.lastName = user.last_name;
      if (!user.lastName && user.lastname) user.lastName = user.lastname;
      if (!user.phone && user.phone_number) user.phone = user.phone_number;

      // Handle full_name or fullname - split into firstName and lastName
      const fullNameField = user.full_name || user.fullname || user.fullName;
      if (fullNameField && (!user.firstName || !user.lastName)) {
        const nameParts = fullNameField.trim().split(/\s+/);
        if (!user.firstName) {
          user.firstName = nameParts[0] || '';
        }
        if (!user.lastName) {
          user.lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
        }
      }

      // Add source
      user.source = source;

      // Only add if has required fields
      if (user.email && user.firstName && user.lastName) {
        users.push(user);
      }
    }

    return users;
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to import',
        variant: 'destructive',
      });
      return;
    }

    setImporting(true);
    setProgress(0);
    setResults(null);

    try {
      // Read file
      const text = await file.text();
      const users = parseCSV(text);

      if (users.length === 0) {
        toast({
          title: 'No valid users found',
          description: 'Make sure your CSV has email, firstName, and lastName columns',
          variant: 'destructive',
        });
        setImporting(false);
        return;
      }

      console.log(`Parsed ${users.length} users from CSV`);

      // Process in batches of 100
      const batchSize = 100;
      const batches = [];
      for (let i = 0; i < users.length; i += batchSize) {
        batches.push(users.slice(i, i + batchSize));
      }

      const allResults: ImportResult[] = [];
      let totalSuccessful = 0;
      let totalFailed = 0;
      let totalSkipped = 0;

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        setProgress(((i + 1) / batches.length) * 100);

        try {
          const response = await apiRequest('POST', '/api/bulk-import/users', {
            users: batch
          });
          const data: ImportResponse = await response.json();
          
          if (data?.results?.details) {
            allResults.push(...data.results.details);
            totalSuccessful += data.results.successful || 0;
            totalFailed += data.results.failed || 0;
            totalSkipped += data.results.skipped || 0;
          }
        } catch (batchError: any) {
          console.error('Batch import error:', batchError);
          // Mark all users in this batch as failed
          batch.forEach((user: any) => {
            allResults.push({
              email: user.email,
              status: 'failed',
              error: batchError.message || 'Unknown error'
            });
            totalFailed++;
          });
        }
      }

      // Set final results
      const finalResults: ImportResponse = {
        success: totalFailed === 0,
        message: `Import completed: ${totalSuccessful} created, ${totalFailed} failed, ${totalSkipped} skipped`,
        results: {
          total: users.length,
          successful: totalSuccessful,
          failed: totalFailed,
          skipped: totalSkipped,
          details: allResults,
        },
      };

      setResults(finalResults);
      setProgress(100);

      toast({
        title: 'Import completed',
        description: finalResults.message,
        variant: totalFailed === 0 ? 'default' : 'destructive',
      });
    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: 'Import failed',
        description: error.message || 'An error occurred during import',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = () => {
    if (!results) return;

    const failedUsers = results.results.details.filter(r => r.status === 'failed');
    const csvContent = [
      'Email,Status,Error',
      ...failedUsers.map(u => `${u.email},${u.status},"${u.error || 'Unknown error'}"`),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-errors-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Filter imported users by source
  const filteredUsers = importedUsers.filter(user => {
    if (!filterSource || filterSource.trim() === '') return true;
    return user.source?.toLowerCase().includes(filterSource.toLowerCase());
  });
  
  console.log('[BULK IMPORT] Import users state:', importedUsers.length);
  console.log('[BULK IMPORT] Filter source:', filterSource);
  console.log('[BULK IMPORT] Filtered users count:', filteredUsers.length);

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Bulk User Import</h1>
        <p className="text-muted-foreground">
          Upload a CSV file to import multiple users at once
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="import">
            <Upload className="w-4 h-4 mr-2" />
            Import Users
          </TabsTrigger>
          <TabsTrigger value="view">
            <UsersIcon className="w-4 h-4 mr-2" />
            View Imported Users
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> Bulk imported users will NOT receive automated welcome emails or SMS messages. 
              They will be created silently with verified accounts.
            </AlertDescription>
          </Alert>

      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Instructions</CardTitle>
            <CardDescription>How to prepare your import file</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <div className="text-primary mt-1">1.</div>
              <div>
                <p className="font-medium">Required Columns:</p>
                <ul className="text-sm text-muted-foreground list-disc ml-4 mt-1">
                  <li>email</li>
                  <li>firstName (or first_name) + lastName (or last_name)</li>
                  <li>OR full_name (will auto-split into first and last name)</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="text-primary mt-1">2.</div>
              <div>
                <p className="font-medium">Optional Columns:</p>
                <ul className="text-sm text-muted-foreground list-disc ml-4 mt-1">
                  <li>phone (or phone_number)</li>
                  <li>marketingConsent (true/false)</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="text-primary mt-1">3.</div>
              <div>
                <p className="font-medium">File Format:</p>
                <p className="text-sm text-muted-foreground">CSV file with headers in the first row</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Example CSV</CardTitle>
            <CardDescription>Download a template to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium mb-2">Option 1: Separate names</p>
                <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`email,firstName,lastName,phone
john@example.com,John,Doe,+27123456789`}
                </pre>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Option 2: Full name</p>
                <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`email,full_name,phone
jane@example.com,Jane Smith,+27987654321`}
                </pre>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                const csv = 'email,full_name,phone,marketingConsent\njohn@example.com,John Doe,+27123456789,true\njane@example.com,Jane Smith,+27987654321,false\n';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'bulk-import-template.csv';
                a.click();
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload File</CardTitle>
          <CardDescription>Select a CSV file to import users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Source</label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g., bulk_import"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be stored in the database to track where users came from
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">CSV File</label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={importing}
                className="flex-1"
              />
              {file && (
                <div className="text-sm text-muted-foreground">
                  {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>
          </div>

          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Importing...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          <Button
            onClick={handleImport}
            disabled={!file || importing}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing...' : 'Start Import'}
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
            <CardDescription>Summary of the import process</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold">{results.results.total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{results.results.successful}</div>
                <div className="text-sm text-muted-foreground">Created</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{results.results.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{results.results.skipped}</div>
                <div className="text-sm text-muted-foreground">Skipped</div>
              </div>
            </div>

            {results.results.failed > 0 && (
              <Button
                variant="outline"
                onClick={downloadErrorReport}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Error Report
              </Button>
            )}

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">Email</th>
                    <th className="text-left p-3 text-sm font-medium">Status</th>
                    <th className="text-left p-3 text-sm font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.details.map((result, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-3 text-sm">{result.email}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {result.status === 'success' && (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-600">Success</span>
                            </>
                          )}
                          {result.status === 'failed' && (
                            <>
                              <XCircle className="w-4 h-4 text-red-600" />
                              <span className="text-sm text-red-600">Failed</span>
                            </>
                          )}
                          {result.status === 'skipped' && (
                            <>
                              <Info className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm text-yellow-600">Skipped</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {result.userId && `User ID: ${result.userId}`}
                        {result.error && (
                          <span className="text-red-600">{result.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="view" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Imported Users</CardTitle>
                  <CardDescription>View all users imported via bulk import</CardDescription>
                </div>
                <Button onClick={fetchImportedUsers} disabled={loadingUsers}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingUsers ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  placeholder="Filter by source (e.g., bulk_import)"
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="max-w-sm"
                />
              </div>

              {loadingUsers ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-2">No imported users found</p>
                  <p className="text-sm text-muted-foreground">
                    {importedUsers.length === 0 
                      ? 'No users with source = "bulk_import" exist in the database'
                      : `${importedUsers.length} user(s) available, but filtered out by your search`
                    }
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[600px] overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-muted sticky top-0">
                          <tr>
                            <th className="text-left p-3 text-sm font-medium">Username</th>
                            <th className="text-left p-3 text-sm font-medium">Email</th>
                            <th className="text-left p-3 text-sm font-medium">Name</th>
                            <th className="text-left p-3 text-sm font-medium">Source</th>
                            <th className="text-left p-3 text-sm font-medium">Created</th>
                            <th className="text-left p-3 text-sm font-medium">Last Login</th>
                            <th className="text-left p-3 text-sm font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map((user) => (
                            <tr key={user.id} className="border-t hover:bg-muted/50">
                              <td className="p-3 text-sm font-mono">{user.username}</td>
                              <td className="p-3 text-sm">{user.email}</td>
                              <td className="p-3 text-sm">
                                {user.firstName || ''} {user.lastName || ''}
                              </td>
                              <td className="p-3 text-sm">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {user.source || 'N/A'}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {user.createdAt ? formatDistanceToNow(new Date(user.createdAt), { addSuffix: true }) : 'N/A'}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {user.lastLoginTimestamp 
                                  ? formatDistanceToNow(new Date(user.lastLoginTimestamp), { addSuffix: true })
                                  : <span className="italic">Never logged in</span>
                                }
                              </td>
                              <td className="p-3 text-sm">
                                {user.isActive ? (
                                  <span className="inline-flex items-center gap-1 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-red-600">
                                    <XCircle className="w-4 h-4" />
                                    Inactive
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

